import { modifier } from 'ember-modifier';

type Positional = [];

type Named = {
  offset?: number; // Offset in pixels (e.g., for navbar height)
  minHeight?: number; // Minimum height in pixels
  mobileBreakpoint?: number; // Disable on screens narrower than this (default: 640 = sm)
  disabled?: boolean;
};

/**
 * Modifier that sets an element's height to fill the viewport.
 * Uses window.innerHeight for accurate mobile browser support
 * (accounts for dynamic browser chrome like address bars).
 *
 * On mobile (below mobileBreakpoint), it allows content to flow naturally.
 *
 * Usage:
 *   {{fill-viewport}} - fills viewport on desktop, auto on mobile
 *   {{fill-viewport offset=78}} - fills viewport minus 78px (navbar)
 *   {{fill-viewport mobileBreakpoint=768}} - use md breakpoint for mobile
 */
export default modifier<HTMLElement, Positional, Named>(function fillViewport(
  element,
  [],
  { offset = 0, minHeight = 0, mobileBreakpoint = 640, disabled = false } = {},
) {
  if (disabled) {
    element.style.height = '';
    element.style.minHeight = '';
    return;
  }

  const updateHeight = () => {
    const isMobile = window.innerWidth < mobileBreakpoint;

    if (isMobile) {
      // On mobile, let content flow naturally
      element.style.height = '';
      element.style.minHeight = '';
    } else {
      // On desktop, fill the viewport
      const vh = window.innerHeight;
      const computedHeight = Math.max(vh - offset, minHeight);
      element.style.height = `${computedHeight}px`;
    }
  };

  // Initial update
  updateHeight();

  // Update on resize and orientation change
  window.addEventListener('resize', updateHeight);
  window.addEventListener('orientationchange', updateHeight);

  // Also update when the visual viewport changes (mobile keyboard, etc.)
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', updateHeight);
  }

  return () => {
    window.removeEventListener('resize', updateHeight);
    window.removeEventListener('orientationchange', updateHeight);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', updateHeight);
    }
    element.style.height = '';
    element.style.minHeight = '';
  };
});



